import { useState } from "react";
import { FormAlert, FormCard, FormDialog, FormSection } from "@NP-Webber/crm-kit";
import { shellApi } from "../../api/shellApi";
import { toClientApiError } from "../../api/httpClient";
import { QuietButton } from "../../ui/QuietButton";
import { useChat } from "./ChatProvider";

type Props = {
  open: boolean;
  onClose: () => void;
};

type BusyState = {
  conversationId: string;
  action: "accept" | "reject";
};

export function PendingChatsModal({ open, onClose }: Props) {
  const { pending, reloadPending, reloadConversations, openConversation } = useChat();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<BusyState | null>(null);

  const accept = async (conversationId: string) => {
    setBusy({ conversationId, action: "accept" });
    setError(null);
    try {
      await shellApi.acceptPendingConversation(conversationId);
      await reloadPending().catch(() => undefined);
      await reloadConversations().catch(() => undefined);
      onClose();
      // Open after close so a follow-up load failure does not look like a failed approve.
      await openConversation(conversationId).catch(() => undefined);
    } catch (e) {
      setError(toClientApiError(e).message);
    } finally {
      setBusy(null);
    }
  };

  const reject = async (conversationId: string) => {
    setBusy({ conversationId, action: "reject" });
    setError(null);
    try {
      await shellApi.rejectPendingConversation(conversationId);
      await reloadPending().catch(() => undefined);
    } catch (e) {
      setError(toClientApiError(e).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <FormDialog open={open} onClose={onClose} size="sm">
      <FormCard title="הזמנות ממתינות" onCancel={onClose} cancelLabel="סגור">
        <FormSection>
          {error ? <FormAlert severity="error">{error}</FormAlert> : null}
          {pending.length === 0 ? (
            <p className="chat-pending__empty">אין הזמנות ממתינות</p>
          ) : (
            <ul className="chat-pending__list">
              {pending.map((p) => {
                const rowBusy = busy?.conversationId === p.conversationId;
                return (
                  <li key={p.membershipId} className="chat-pending__item">
                    <div>
                      <strong>{p.name?.trim() || (p.isGroup ? "קבוצה" : "שיחה ישירה")}</strong>
                      <div className="chat-pending__meta">
                        מאת {p.invitedBy?.displayName || "משתמש"}
                      </div>
                    </div>
                    <div className="chat-pending__actions">
                      <QuietButton
                        type="button"
                        tone="accent"
                        disabled={rowBusy}
                        loading={rowBusy && busy?.action === "accept"}
                        onClick={() => void accept(p.conversationId)}
                      >
                        {rowBusy && busy?.action === "accept" ? "מאשר…" : "אישור"}
                      </QuietButton>
                      <QuietButton
                        type="button"
                        tone="danger"
                        disabled={rowBusy}
                        loading={rowBusy && busy?.action === "reject"}
                        onClick={() => void reject(p.conversationId)}
                      >
                        {rowBusy && busy?.action === "reject" ? "דוחה…" : "דחייה"}
                      </QuietButton>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </FormSection>
      </FormCard>
    </FormDialog>
  );
}
